import {
  indexDBService,
  localizationCache,
  useLangStore,
  useProcessSocketStore,
  useUserStore,
  useAutoTranslation,
  Sentry,
} from "@/entities"
import { getDictionary, getDictionaryVersion } from "@/shared/api/dictionary"
import { getBootstrapSession } from "@/shared/api/bootstrap"
import { UiFullscreenSpinner } from "@/shared/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ErrorPage } from "@/pages"
import {
  isDefinitiveSessionRejection,
  refreshBrowserSession,
} from "@/shared/api/api-instance"
import { DISCOVERED_TRANSLATION_KEYS } from "@/shared/constants"
import { getInfrastructureStatus } from "@/shared/api/infrastructure"
import { MaintenanceContent } from "../maintenance"

// How often an open tab re-asks the status service. Mirrors the mobile app's
// startup check, plus polling so maintenance can be switched on from the
// telegram bot without killing the web container to push users out.
const MAINTENANCE_POLL_MS = 60_000

const ERROR_MESSAGES = {
  NO_DICTIONARY: "No dictionary found",
  UNKNOWN_ERROR: "Unknown error occurred",
}

export const PreloadProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [isDone, setDone] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isMaintenance, setMaintenance] = useState(false)
  const { i18n } = useTranslation()
  const { t } = useAutoTranslation()

  useEffect(() => {
    const initHandler = async () => {
      const userStoreState = useUserStore.getState()
      const langStoreState = useLangStore.getState()

      try {
        // Before anything that needs the API: during maintenance the API is
        // down and the dictionary/session calls below would only surface as
        // an init error. Unknown status (service unreachable) fails open.
        const infrastructure = await getInfrastructureStatus()
        if (infrastructure?.isMaintenance) {
          setMaintenance(true)
          setDone(true)
          return
        }

        const cachedDictionary = await localizationCache.getDictionary(
          langStoreState.lang
        )
        const downloadedDictionary = cachedDictionary
          ? null
          : await getDictionary(langStoreState.lang)
        const currentDictionary =
          cachedDictionary?.data ?? downloadedDictionary?.data ?? {}
        if (Object.keys(currentDictionary).length === 0) {
          throw new Error(ERROR_MESSAGES.NO_DICTIONARY)
        }
        i18n.addResourceBundle(
          langStoreState.lang,
          "translation",
          currentDictionary
        )
        await i18n.changeLanguage(langStoreState.lang)

        if (!cachedDictionary && downloadedDictionary) {
          await localizationCache.saveDictionary(
            langStoreState.lang,
            downloadedDictionary.data,
            downloadedDictionary.version
          )
        }

        let bootstrapTranslationVersion: number | null = null
        const restoredAccessToken = await refreshBrowserSession()
        if (restoredAccessToken) {
          try {
            const session = await getBootstrapSession(restoredAccessToken)
            bootstrapTranslationVersion = session.translationVersion
            await userStoreState.login(session.accessToken, session.options)
            if (session.processing.shouldReconnectSocket) {
              void useProcessSocketStore.getState().connectToProcessSocket()
            }
          } catch (bootstrapError) {
            // Terminal only when the session was actually rejected: a
            // definitive server code, or the API layer already dropped the
            // in-memory token after a definitive refresh rejection (the
            // server clears the invalid HttpOnly cookie itself). A transient
            // failure (network, 5xx during a redeploy) must not revoke the
            // cookie session — surface the init error instead; a reload
            // restores the session.
            const rejected =
              isDefinitiveSessionRejection(bootstrapError) ||
              !useUserStore.getState().access_token
            if (rejected) {
              userStoreState.logout({ revoke: false })
            } else {
              throw bootstrapError
            }
          }
        }
        await indexDBService.init()

        setDone(true)

        void (async () => {
          const serverVersion =
            bootstrapTranslationVersion ?? (await getDictionaryVersion())
          if (serverVersion === null) return
          const latestCached = await localizationCache.getDictionary(
            langStoreState.lang
          )
          if (latestCached?.translationVersion === serverVersion) return

          const dictionary = await getDictionary(langStoreState.lang)
          if (dictionary && Object.keys(dictionary.data).length) {
            await localizationCache.saveDictionary(
              langStoreState.lang,
              dictionary.data,
              dictionary.version
            )
            i18n.addResourceBundle(
              langStoreState.lang,
              "translation",
              dictionary.data,
              true,
              true
            )
          }
        })()
      } catch (err) {
        const errorObj =
          err instanceof Error ? err : new Error(ERROR_MESSAGES.UNKNOWN_ERROR)
        Sentry.captureException(errorObj, {
          tags: { phase: "preload_init" },
        })
        setError(errorObj)
        setDone(true) // Set done to true so we can show the error UI
      }
    }

    initHandler()
  }, [i18n])

  // Keep watching after startup: flip to the maintenance screen when the
  // flag goes up, and reload once it is cleared so the app starts clean.
  useEffect(() => {
    if (!isDone) return
    let cancelled = false

    const check = async () => {
      if (document.visibilityState === "hidden") return
      const status = await getInfrastructureStatus()
      if (cancelled || !status) return
      if (status.isMaintenance && !isMaintenance) {
        setMaintenance(true)
      } else if (!status.isMaintenance && isMaintenance) {
        window.location.reload()
      }
    }

    const interval = setInterval(check, MAINTENANCE_POLL_MS)
    document.addEventListener("visibilitychange", check)
    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener("visibilitychange", check)
    }
  }, [isDone, isMaintenance])

  if (!isDone) {
    return <UiFullscreenSpinner />
  }

  if (isMaintenance) {
    return <MaintenanceContent variant="server" />
  }

  if (error) {
    return (
      <ErrorPage
        errorMessage={error.message}
        title={t(DISCOVERED_TRANSLATION_KEYS.INITIALIZATION_ERROR)}
      />
    )
  }

  return children
}
